import type { MenuProps, NotificationProps } from "@mantine/core";
import classes from "./productChrome.module.css";
import fieldClasses from "./productFields.module.css";
import { brandColorTokens as colors } from "./brandColorTokens";
import { brandedButtonProps, brandSelectDropdownClassNames, brandSelectDropdownStyles } from "./brandControls";
import { brandEditableFieldClassNames, brandEditableFieldStyles } from "./brandFields";

/** Shared product surfaces; apply at call sites, not through global Mantine overrides. */
export const productCardProps = {
  withBorder: true,
  padding: "xl",
  radius: "lg",
  styles: {
    root: { backgroundColor: colors.facilitatorSectionTile, borderColor: colors.facilitatorSectionTileBorder },
  },
} as const;

export const productPrimaryButtonProps = brandedButtonProps(
  colors.facilitatorPrimaryCta,
  colors.facilitatorPrimaryCtaHover,
  colors.facilitatorPrimaryCtaText,
);
export const productSecondaryButtonProps = brandedButtonProps(
  colors.facilitatorHomeResourceOpenButton,
  colors.facilitatorHomeResourceOpenButtonHover,
  colors.bodyText,
);

/** CSS bridge for the same L1 roles in app-specific CSS modules and portaled controls. */
export const productSurfaceCssVars = {
  "--product-text": colors.bodyText,
  "--product-text-secondary": colors.textSecondary,
  "--product-text-muted": colors.textMuted,
  "--product-paper": colors.facilitatorMainContentPaper,
  "--product-surface": colors.facilitatorSectionTile,
  "--product-surface-hover": colors.facilitatorSectionTileHover,
  "--product-selected": colors.facilitatorSidebarNavActive,
  "--product-border": colors.surfaceBorder,
  "--product-border-strong": colors.profileFieldHoverBorder,
  "--product-accent": colors.facilitatorPageHeading,
  "--product-focus": colors.collectQuestionAccent,
  "--product-chart": colors.chartSeries,
  "--product-chart-track": colors.chartTrack,
  "--product-positive": colors.chartPositive,
  "--product-negative": colors.chartNegative,
  "--product-signal": colors.chartSignal,
  "--product-highlight": colors.highlightSurface,
  "--product-highlight-border": colors.highlightSurfaceBorder,
  "--product-on-dark": colors.slideTextOnDark,
} as const;

export const productFieldProps = {
  classNames: { ...brandEditableFieldClassNames, wrapper: fieldClasses.productEditableControl },
  styles: brandEditableFieldStyles,
};
export const productSelectProps = {
  classNames: {
    ...brandEditableFieldClassNames,
    ...brandSelectDropdownClassNames,
    wrapper: fieldClasses.productEditableControl,
  },
  styles: { ...brandEditableFieldStyles, ...brandSelectDropdownStyles },
};

export const productQuietButtonProps = brandedButtonProps(
  "transparent",
  colors.facilitatorSidebarNavActive,
  colors.facilitatorPageHeading,
);

/** Explicit colors also work when the notification is portaled outside product chrome. */
export const productNotificationProps = {
  radius: "lg",
  p: "lg",
  withBorder: true,
  styles: {
    root: {
      backgroundColor: colors.slideNotificationBackground,
      borderColor: colors.slideNotificationBorder,
      color: colors.bodyText,
    },
    title: { color: colors.bodyText, fontWeight: 600 },
    description: { color: colors.bodyText },
    icon: { backgroundColor: colors.facilitatorSidebarNavActive, color: colors.facilitatorPageHeading },
    closeButton: { color: colors.bodyText },
  },
} satisfies NotificationProps;

export const productPaginationProps = { classNames: { control: classes.paginationControl } };
export const productSegmentedControlProps = {
  classNames: { root: classes.segmentedRoot, label: classes.segmentedLabel, indicator: classes.segmentedIndicator },
};
export const productSwitchProps = { classNames: { track: classes.switchTrack } };

/** Local variables keep portaled menus on the product palette without global overrides. */
const productMenuCssVars = {
  ...productSurfaceCssVars,
  "--product-menu-hover": colors.facilitatorListOptionHover,
  "--product-menu-selected": colors.facilitatorListOptionSelected,
  "--product-menu-danger": colors.profileRemoveButtonText,
} as const;

export const productMenuProps: MenuProps = {
  width: 280,
  radius: "lg",
  offset: 8,
  middlewares: {
    size: {
      padding: 8,
      apply({ availableHeight, elements }) {
        elements.floating.style.setProperty("--product-menu-available-height", `${Math.max(0, availableHeight)}px`);
      },
    },
  },
  classNames: {
    dropdown: classes.menuDropdown,
    item: classes.menuItem,
    itemLabel: classes.menuItemLabel,
    itemSection: classes.menuItemSection,
    label: classes.menuLabel,
    divider: classes.menuDivider,
  },
  styles: { dropdown: productMenuCssVars },
};

export const productMenuTriggerProps = {
  w: "100%",
  className: classes.menuTrigger,
  style: productMenuCssVars,
} as const;

export const productMenuLabelClassName = classes.menuTriggerLabel;
export const productMenuIconProps = { size: 18, stroke: 1.75, "aria-hidden": true } as const;
